import { Col, Row } from 'antd';
import Button from 'components/ui/base/button';
import FormInput from 'components/ui/composed/declarations/formInput/DeclarationInput';
import { useFormik } from 'formik';
import useModalMultipleCards from 'hooks/useModalMultipleCards';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ImportGoodsInformation } from 'store/declarations/ireland/refunds/deposits/ireland-refund-deposit';
import { CardContent } from 'views/declarations/common/box44/Box44Card.styles';
import FormCard from 'views/declarations/common/cards/FormCard';
import { DeclarationFormCardProps } from 'views/declarations/common/declaration.form.card';
import CardList from 'views/declarations/common/list-card/CardList';
import ModalEditCardListItem from 'views/declarations/common/modal-edit-list-item/ModalEditCardListItem';
import { getFormikProps } from 'views/declarations/utils/form-utils';
import * as Yup from 'yup';

const ImportGoodsInformationCard: FC<DeclarationFormCardProps> = ({
    selectCard,
    keyCard,
    defaultOpen = false,
    viewOnly = false,
    expandAll = false,
    cardTotal,
    cardNumber,
    getFieldHelpers,
    getFieldProps,
    getFieldMeta,
}) => {
    const { t } = useTranslation('form');
    const formik = useFormik<ImportGoodsInformation>({
        initialValues: {},
        validationSchema: Yup.object({}),
        validateOnMount: true,
        enableReinitialize: true,
        onSubmit: (values) => {
            addItem(values);
        },
    });
    const {
        setValues,
        getFieldProps: formGetFieldProps,
        getFieldHelpers: formGetFieldHelpers,
        getFieldMeta: formGetFieldMeta,
        resetForm,
    } = formik;

    const { modalVisible, handleEdit, handleCancel, onClickAddItem, handleDelete, addItem } = useModalMultipleCards({
        formikProps: {
            fieldProps: getFieldProps('importGoodsInformation'),
            fieldHelper: getFieldHelpers('importGoodsInformation'),
            fieldMeta: getFieldMeta ? getFieldMeta('importGoodsInformation') : undefined,
        },
        setValues,
        resetForm,
    });

    const goodsInformationDataList = useMemo(() => {
        const list = getFieldProps('importGoodsInformation').value;
        if (Array.isArray(list)) {
            return list.map((obj: ImportGoodsInformation, index) => {
                return [
                    {
                        field: t('goodsItems.goodsDescription'),
                        value: obj.goodsDescription,
                    },
                    {
                        field: t('remissionRefund.combinedNomenclatureCode'),
                        value: obj.commodityCode?.combinedNomenclatureCode ?? '-',
                    },
                    {
                        field: t('depositRefund.taricCode'),
                        value: obj.commodityCode?.taricCode ?? '-',
                    },
                    {
                        field: t('quantity'),
                        value: obj.goodsQuantity?.quantity?.toString() ?? '-',
                    },
                    {
                        field: t('depositRefund.importGoodsItems.measurementUnit'),
                        value: obj.goodsQuantity?.measurementUnit ?? '-',
                    },
                ];
            });
        }
        return [];
    }, [getFieldProps, t]);

    return (
        <section
            id={'deposit-import-goods-information-form-card'}
            onClick={() => {
                if (selectCard) {
                    selectCard('deposit-import-goods-information-form-card');
                }
            }}
        >
            <FormCard
                key={keyCard}
                keyCard={keyCard}
                defaultOpen={defaultOpen}
                viewOnly={viewOnly}
                title={t('depositRefund.importGoodsItems.title')}
                expandAll={expandAll}
                cardNumber={cardNumber}
                total={cardTotal}
                showRightSideCardState={false}
            >
                <CardContent>
                    <CardList
                        data={goodsInformationDataList}
                        onDelete={handleDelete}
                        onEdit={handleEdit}
                        viewOnly={viewOnly ?? false}
                    />
                    {!viewOnly && <Button onClick={onClickAddItem}>Add item</Button>}
                </CardContent>
            </FormCard>
            <ModalEditCardListItem visible={modalVisible} onCancel={handleCancel} width={1020} formik={formik}>
                <Row gutter={12}>
                    <Col xs={24}>
                        <FormInput
                            viewOnly={viewOnly}
                            {...getFormikProps(`commodityCode.combinedNomenclatureCode`, {
                                getFieldHelpers: formGetFieldHelpers,
                                getFieldProps: formGetFieldProps,
                                getFieldMeta: formGetFieldMeta,
                            })}
                            label={t('remissionRefund.combinedNomenclatureCode')}
                        />
                        <FormInput
                            viewOnly={viewOnly}
                            {...getFormikProps(`commodityCode.taricCode`, {
                                getFieldHelpers: formGetFieldHelpers,
                                getFieldProps: formGetFieldProps,
                                getFieldMeta: formGetFieldMeta,
                            })}
                            label={t('depositRefund.taricCode')}
                        />
                        <FormInput
                            viewOnly={viewOnly}
                            {...getFormikProps(`goodsDescription`, {
                                getFieldHelpers: formGetFieldHelpers,
                                getFieldProps: formGetFieldProps,
                                getFieldMeta: formGetFieldMeta,
                            })}
                            label={t('goodsItems.goodsDescription')}
                        />
                        <FormInput
                            viewOnly={viewOnly}
                            {...getFormikProps(`valueOfGoods`, {
                                getFieldHelpers: formGetFieldHelpers,
                                getFieldProps: formGetFieldProps,
                                getFieldMeta: formGetFieldMeta,
                            })}
                            label={t('depositRefund.importGoodsItems.valueOfGoods')}
                        />
                        <FormInput
                            viewOnly={viewOnly}
                            {...getFormikProps(`goodsQuantity.quantity`, {
                                getFieldHelpers: formGetFieldHelpers,
                                getFieldProps: formGetFieldProps,
                                getFieldMeta: formGetFieldMeta,
                            })}
                            label={t('quantity')}
                        />
                        <FormInput
                            viewOnly={viewOnly}
                            {...getFormikProps(`goodsQuantity.measurementUnit`, {
                                getFieldHelpers: formGetFieldHelpers,
                                getFieldProps: formGetFieldProps,
                                getFieldMeta: formGetFieldMeta,
                            })}
                            label={t('depositRefund.importGoodsItems.measurementUnit')}
                        />
                    </Col>
                </Row>
            </ModalEditCardListItem>
        </section>
    );
};
export default ImportGoodsInformationCard;
